// Избранные билеты (хранятся в браузере) и проверка их цены
import type { Currency, FlightSearchResult, Ticket } from '@sayohat/shared';
import { api } from '../../shared/lib/api';
import { bus } from '../../shared/lib/bus';
import { store } from '../../shared/lib/storage';
import { createStore, useStore } from '../../shared/lib/store';
import type { SavedQuery, SavedTicket, SearchQuery } from '../../shared/lib/types';

const KEY = 'favorites';
const favStore = createStore({ items: store.get<SavedTicket[]>(KEY, []), refreshing: false });

export const hasFavorite = (id: string): boolean => favStore.get().items.some((f) => f.ticket.id === id);
export const allFavorites = (): SavedTicket[] => favStore.get().items;

export const useFavorites = () => useStore(favStore);
export const useIsFavorite = (id: string): boolean => useStore(favStore, (s) => s.items.some((f) => f.ticket.id === id));

function persist(items: SavedTicket[]): void {
  // Прошедшие рейсы убираем автоматически
  const now = new Date().toISOString().slice(0, 10);
  const kept = items.filter((f) => f.ticket.to.departureAt.slice(0, 10) >= now).slice(0, 100);
  store.set(KEY, kept);
  favStore.set({ items: kept });
  bus.emit('favorites:changed', kept.length);
}

/** Добавить/убрать билет; возвращает, в избранном ли он теперь */
export function toggleFavorite(ticket: Ticket, { currency, query }: { currency: Currency; query: Partial<SearchQuery> | SavedQuery }): boolean {
  const items = favStore.get().items;
  if (items.some((f) => f.ticket.id === ticket.id)) {
    persist(items.filter((f) => f.ticket.id !== ticket.id));
  } else {
    // Запоминаем точные даты рейса — даже если искали «на весь месяц»
    const exact: SavedQuery = {
      ...query,
      departureExact: ticket.to.departureAt.slice(0, 10),
      retExact: ticket.back ? ticket.back.departureAt.slice(0, 10) : '',
    };
    persist([{ ticket, currency, query: exact, savedAt: new Date().toISOString(), savedPrice: ticket.price }, ...items]);
  }
  return hasFavorite(ticket.id);
}

/** Убрать прошедшие и сообщить счётчик (при старте и открытии раздела) */
export const syncFavorites = (): void => persist(favStore.get().items);

function searchParams(q: SavedQuery & { currency?: Currency }): URLSearchParams {
  const p = new URLSearchParams({
    origin: q.origin || '', destination: q.destination || '', departure: q.departureExact || q.departure || '',
    currency: q.currency || 'usd', direct: q.direct ? 'true' : 'false',
    adults: String(q.adults || 1), children: String(q.children || 0), infants: String(q.infants || 0),
  });
  const ret = q.departureExact ? q.retExact : q.ret;
  if (ret) p.set('ret', ret);
  return p;
}

/** Ссылка на поиск этого билета на сайте */
export function searchUrl(q: SavedQuery): string {
  const p = new URLSearchParams({ from: q.origin || '', to: q.destination || '', depart: q.departureExact || q.departure || '', cur: q.currency || 'usd' });
  const ret = q.departureExact ? q.retExact : q.ret;
  if (ret) p.set('ret', ret);
  return `/?${p}#flights`;
}

function sameFlight(a: Ticket, b: Ticket): boolean {
  return a.airline === b.airline && String(a.flightNumber) === String(b.flightNumber)
    && a.to.departureAt === b.to.departureAt
    && (a.back ? a.back.departureAt : '') === (b.back ? b.back.departureAt : '');
}

// Повторяем поиск для каждого сохранённого билета и ищем тот же рейс
export async function refreshFavorites(): Promise<void> {
  favStore.set({ refreshing: true });
  const items = favStore.get().items.map((f) => ({ ...f }));
  const groups = new Map<string, { p: URLSearchParams; list: SavedTicket[] }>();
  for (const f of items) {
    const p = searchParams({ ...f.query, currency: f.currency });
    const key = p.toString();
    if (!groups.has(key)) groups.set(key, { p, list: [] });
    groups.get(key)!.list.push(f);
  }
  for (const { p, list } of groups.values()) {
    try {
      const data = await api<FlightSearchResult>(`/api/search?${p}`);
      const pool = [...data.tickets, ...(data.nearby || [])];
      for (const f of list) {
        const same = pool.find((t) => sameFlight(t, f.ticket));
        f.currentPrice = same ? same.price : null;
        f.currentTicket = same || null;
        f.checkedAt = new Date().toISOString();
      }
    } catch {
      // этот поиск не удался — оставляем прежние данные
    }
  }
  persist(items);
  favStore.set({ refreshing: false });
}
